import React from 'react';
import { Button, Popconfirm } from 'antd';
import { DeleteOutlined, ProjectOutlined, ToolOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Feed } from '../../services/feedService';
import { PersonAvatar, Pill } from '../ui';
import { downloadUrl } from '../ui/ImageLightbox';
import './FeedCard.css';

/**
 * One work-feed post, shared by the dashboard and the Feeds page.
 *
 * Images are rendered from `thumbnailUrls` (800px WebP copies) — the full image is only
 * downloaded when someone opens the lightbox.
 */
const FeedCard: React.FC<{
  feed: Feed;
  /** Opens the viewer at that photo. */
  onOpenImage: (index: number) => void;
  onDelete?: (id: number) => void;
  deleting?: boolean;
  /** Feeds page shows the full date; the dashboard only needs the time. */
  showDate?: boolean;
  thumbSize?: number;
}> = ({ feed, onOpenImage, onDelete, deleting, showDate, thumbSize = 104 }) => {
  const { t, i18n } = useTranslation();
  const employee = feed.user?.employee;
  const images = feed.imageUrls ?? [];
  // "villa-mueller-2026-09-22" — what a saved photo is called
  const downloadName = [feed.project?.name, dayjs(feed.created_at).format('YYYY-MM-DD')].filter(Boolean).join(' ');
  const typeLabel =
    feed.type === 'after' ? t('dashboard.feedAfter') : feed.type === 'before' ? t('dashboard.feedBefore') : feed.type;

  return (
    <article className="feed-card">
      <header className="feed-card-header">
        <PersonAvatar
          name={employee?.name}
          surname={employee?.surname}
          imageUrl={employee?.thumbnailUrl || employee?.imageUrl}
          size={40}
        />
        <div className="e22-row-body">
          <div className="e22-row-title">{feed.user?.name}</div>
          <div className="feed-card-meta">
            <span className="feed-card-project">
              <ProjectOutlined aria-hidden /> {feed.project?.name}
            </span>
            {feed.type ? <Pill tone={feed.type === 'after' ? 'success' : 'primary'}>{typeLabel}</Pill> : null}
          </div>
        </div>
        <time className="feed-card-time" dateTime={feed.created_at}>
          {showDate
            ? new Date(feed.created_at).toLocaleString(i18n.language, {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })
            : dayjs(feed.created_at).format('HH:mm')}
        </time>
        {onDelete ? (
          <Popconfirm
            title={t('feeds.deleteConfirm')}
            description={t('feeds.deleteDescription')}
            onConfirm={() => onDelete(feed.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deleting}
              aria-label={t('feeds.deleteFeed')}
            />
          </Popconfirm>
        ) : null}
      </header>

      {feed.content ? <p className="feed-card-content">{feed.content}</p> : null}
      {feed.description ? <p className="feed-card-description">{feed.description}</p> : null}

      {feed.materials_used ? (
        <div className="feed-card-materials">
          <ToolOutlined aria-hidden />
          <span>
            <strong>{t('dashboard.materials')}:</strong> {feed.materials_used}
          </span>
        </div>
      ) : null}

      {images.length > 0 ? (
        <div className="e22-image-grid">
          {images.map((url, index) => (
            <div key={url} className="feed-card-thumb" style={{ width: thumbSize, height: thumbSize }}>
            <button
              type="button"
              className="e22-thumb"
              style={{ width: thumbSize, height: thumbSize }}
              onClick={() => onOpenImage(index)}
              aria-label={t('feeds.openPhoto', { number: index + 1 })}
            >
              <img
                src={feed.thumbnailUrls?.[index] ?? url}
                alt=""
                width={thumbSize}
                height={thumbSize}
                loading="lazy"
                decoding="async"
              />
            </button>
            <a
              className="feed-card-download"
              href={downloadUrl(url, images.length > 1 ? `${downloadName}-${index + 1}` : downloadName)}
              title={t('feeds.downloadPhoto')}
              aria-label={t('feeds.downloadPhoto')}
              onClick={(event) => event.stopPropagation()}
            >
              <DownloadOutlined />
            </a>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
};

export default FeedCard;
